<script setup lang="ts">
import { sift } from 'radashi';
import { computed } from 'vue';

import type { BlockData, SectionData } from '@/types';
import ContentContainer from '@/components/layout/ContentContainer.vue';
import { useVisibleAnchor } from '@/composables/useVisibleAnchor';

const props = defineProps<{
  sections: SectionData[];
}>();

const { activeAnchorTarget } = useVisibleAnchor();

type AnchorLink = NonNullable<SectionData['link']>;

interface LinkTree {
  link: AnchorLink;
  subLinks?: LinkTree[];
}

const getLinkTreeFromSection = (section: SectionData | BlockData): LinkTree | undefined => {
  const { link } = section;
  if (!link) return undefined;

  if ('blocks' in section) {
    const subLinks = sift(section.blocks.map(getLinkTreeFromSection));

    if (subLinks.length > 0) return { link, subLinks };
  }
  return { link };
};

const links = computed(() => sift(props.sections.map(getLinkTreeFromSection)));
</script>

<template>
  <ContentContainer class="page-nav-wrap">
    <nav class="page-nav">
      <div class="menu-title subhead-primary">
        Jump to
      </div>
      <ul>
        <li
          v-for="({ link, subLinks }, idx) in links"
          :key="idx"
        >
          <NuxtLink
            :to="`#${link.anchor}`"
            class="page-nav-link font-secondary"
            :class="{ 'is-active': activeAnchorTarget?.id === link.anchor }"
          >
            {{ link.title }}
          </NuxtLink>

          <ul v-if="subLinks?.length">
            <li
              v-for="{ link: subLink } in subLinks"
              :key="subLink.anchor"
            >
              <NuxtLink
                v-if="subLink"
                :to="`#${subLink.anchor}`"
                class="page-nav-sub-link font-primary"
                :class="{ 'is-active': activeAnchorTarget?.id === subLink.anchor }"
              >
                {{ subLink.title }}
              </NuxtLink>
            </li>
          </ul>
        </li>
      </ul>
    </nav>
  </ContentContainer>
</template>

<style lang="scss" scoped>
@use "@/assets/styles/shared/screen" as screen;

.page-nav-wrap {
  pointer-events: none;

  :deep(.content-container) {
    @include screen.gridContainer();

    @include screen.respond(tablet) {
      padding: 0;
    }
  }
}

.menu-title {
  display: none;
  margin: 2rem 0;
  padding: 0 var(--spacing-m);
  text-transform: uppercase;

  @include screen.respond(tablet) {
    display: block;
  }
}
.page-nav {
  pointer-events: all;
  padding-right: var(--spacing-ms);
  background: #fff;
  border-radius: 0.5rem;

  @include screen.grid(3, 8, 4);

  ul {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      margin-bottom: 0;

      ul {
        margin-bottom: var(--spacing-m);

        @include screen.respond(tablet) {
          margin-bottom: 0;
        }
      }
    }
  }

  a {
    color: var(--colour-mid-grey);
    text-decoration: none;
    transition: color 0.5s ease, background-color 0.5s ease;
    padding: 0.8rem var(--spacing-ms) 0.8rem var(--spacing-l);
    position: relative;
    display: block;
    border-radius: 0.2rem;

    &:before {
      content: "";
      width: 0.8rem;
      height: 0.8rem;
      border-radius: 0.4rem;
      display: block;
      background-color: #fff;
      border: 0.1rem solid #000;
      opacity: 0;
      position: absolute;
      top: 50%;
      left: var(--spacing-ms);
      transform: translateY(-50%);
      transition: all 0.5s ease;

      @include screen.respond(tablet) {
        left: var(--spacing-m);
      }
    }

    &:hover {
      color: #000;

      &:before {
        opacity: 1;
      }
    }
    &.page-nav-link {
      font-weight: 500;
    }
    &.page-nav-sub-link {
      &.is-active {
        font-weight: 500;
      }
    }

    &.is-active {
      color: var(--colour-dark-grey);
      background-color: var(--colour-off-white);
      font-weight: bold;

      &:before {
        opacity: 1;
        background-color: #000;
      }
    }
    @include screen.respond(tablet) {
      border-top: var(--colour-light-warm-grey) solid 0.1rem;
      padding-top: var(--spacing-ms);
      padding-bottom: var(--spacing-ms);
      background: #fff;
      border-radius: 0;
    }
  }

  @include screen.respond(tablet) {
    padding-right: 0;
    border-bottom: var(--colour-light-warm-grey) solid 0.1rem;
    margin-bottom: var(--spacing-xl);
  }
}
</style>
